import { FC, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Formik, FormikProps } from "formik"
import { object, string } from "yup"
import { SubscriptionConfigDetails } from "../../../types"
import ManageSubscriptionConfigFormContent from "../../../common/ui/config-admin/config-subscription/manage/form-content"
import Tabs from "../../../common/ui/tabs"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import {
  useDeleteSubscriptionConfigMutation,
  useFetchAllBrandsQuery,
  useFetchSubscriptionConfigDetailsQuery,
  useUpdateOrCreateSubscriptionConfigMutation
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useAppDispatch } from "../../../common/hooks"
import { useConfigMutationFeedback } from "../use-config-mutation-feedback"
import DefaultConfig from "../../../common/ui/config-admin/config-subscription/manage/default-config.json"

const BACK_TAB = "/config-admin?tab=subscription"
const backUrl = (brandId?: string) =>
  brandId ? `${BACK_TAB}&brand_id=${brandId}` : BACK_TAB

const TABS = [
  { id: "features", label: "Features" },
  { id: "defaults", label: "Defaults" },
  { id: "field-overrides", label: "Field Overrides" }
]

const SubscriptionSchema = object({
  name: string().required().min(1),
  enabled: string().required(),
  brand_id: string().required().min(1),
  config: object().required()
})

const SubscriptionEditPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isCreate = id === "new"
  const [tab, setTab] = useState(TABS[0].id)
  const [hasChanges, setHasChanges] = useState(false)

  const { data: brands } = useFetchAllBrandsQuery({ limit: 999 })

  const { data: subscriptionData, isLoading } =
    useFetchSubscriptionConfigDetailsQuery(
      { id: id ?? "" },
      { skip: isCreate || !id }
    )

  const [updateOrCreate, saveResult] =
    useUpdateOrCreateSubscriptionConfigMutation()
  const [deleteSubscription, deleteResult] =
    useDeleteSubscriptionConfigMutation()

  const formRef = useRef<FormikProps<SubscriptionConfigDetails>>(null)

  useConfigMutationFeedback({
    label: "subscription config",
    isCreate,
    saveResult,
    deleteResult,
    navigate,
    backUrl: () => backUrl(formRef.current?.values.brand_id),
    dispatch
  })

  const raw = subscriptionData ?? {
    name: "",
    id: "",
    enabled: "0",
    brand_id: "",
    created: "",
    is_default: "0",
    config: DefaultConfig
  }

  const initialValues: SubscriptionConfigDetails = {
    ...raw,
    name: raw.name || "",
    is_default: raw.is_default || "0",
    config: raw.config ?? DefaultConfig
  }

  const setFormField = (name: string, val: any) => {
    formRef.current?.setFieldValue(name, val)
  }

  const setDetailsField = (field: string, value: any) => {
    const currentConfig = formRef.current?.values.config || {}
    const newConfig = {
      ...currentConfig,
      [field]: value
    }
    formRef.current?.setFieldValue("config", newConfig, false)
    setHasChanges(true)
  }

  const onSubmit = () => {
    if (formRef.current?.values) updateOrCreate(formRef.current.values)
  }

  const onDelete = () => {
    if (subscriptionData?.id) deleteSubscription(subscriptionData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={4} />
  }

  const brandName = brands?.find(b => b.id === raw.brand_id)?.name
  const pageTitle = isCreate
    ? "New subscription config"
    : brandName
    ? `${brandName} Subscription Config`
    : "Subscription config"

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={SubscriptionSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              backLabel="Subscription Configs"
              namePlaceholder="Subscription config name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty || hasChanges}
              isDefault={values.is_default === "1"}
              isEnabled={values.enabled === "1"}
              brands={brands ?? []}
              brandId={values.brand_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("is_default", v ? "1" : "0")}
              onEnabledChange={v => setFormField("enabled", v ? "1" : "0")}
              onBrandChange={val => setFormField("brand_id", val)}
              onDelete={onDelete}
              onCancel={() => navigate(backUrl(values.brand_id))}
              onSubmit={onSubmit}
              onBack={() => navigate(backUrl(values.brand_id))}
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageSubscriptionConfigFormContent
                  values={values}
                  tab={tab}
                  setDetailsField={setDetailsField}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default SubscriptionEditPage
